'use client';
import React from 'react';
import Link from 'next/link';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import { useScrollAnimation } from '@/hooks/useScrollAnimation';

const challenges = [
  {
    tag: '[01]',
    title: 'Patient Data at Scale',
    desc: 'Electronic health records, diagnostic data, and genomic information are among the most sensitive data types in existence. A breach is not just a regulatory event, it is a patient safety issue.',
  },
  {
    tag: '[02]',
    title: 'Medical Device & IoT Security',
    desc: 'Connected devices, wearables, and diagnostic equipment introduce attack vectors that traditional IT security programmes are not designed to handle. OT and IoT security requires a different approach.',
  },
  {
    tag: '[03]',
    title: 'HIPAA, GDPR & NHS DSP Toolkit',
    desc: 'US-facing firms must satisfy HIPAA. UK NHS suppliers must pass the Data Security and Protection Toolkit. GDPR applies across the board. Overlapping obligations, one programme.',
  },
  {
    tag: '[04]',
    title: 'Rapid Product Iteration vs. Security',
    desc: 'HealthTech startups ship fast. Security cannot be a blocker, it must be embedded into the development process from day one, not bolted on before a regulatory review.',
  },
];

const services = [
  { label: 'HIPAA & GDPR Compliance', href: '/services/compliance', desc: 'Continuous compliance programmes covering data governance, access controls, breach notification, and audit evidence, built for health data environments.' },
  { label: 'NHS DSP Toolkit Readiness', href: '/services/accelerator', desc: 'Structured readiness programme for NHS Digital\'s Data Security and Protection Toolkit, including gap assessment and evidence collection.' },
  { label: 'AppSec for Health Applications', href: '/services/appsec', desc: 'SAST, DAST, and SCA for patient-facing applications and clinical systems. Business-logic vulnerabilities in health data flows that automated scanners miss.' },
  { label: 'Threat Modelling for MedTech', href: '/services/threat-modelling', desc: 'Security architecture review for connected medical devices, diagnostic platforms, and health data pipelines, before vulnerabilities reach patients.' },
  { label: 'Bespoke Security Programme', href: '/services/security-programmes', desc: 'A security programme designed for your organisation, your technology, and your regulatory obligations, not a generic framework.' },
  { label: 'Security Focus Programme', href: '/services/security-focus', desc: 'Prioritised, auditor-aligned programme that cuts unnecessary work and focuses your team on controls that close real patient data exposure.' },
];

const outcomes = [
  { metric: 'HIPAA', label: 'Technical safeguards implemented and continuously monitored' },
  { metric: 'GDPR Art.32', label: 'Appropriate technical measures evidenced for health data' },
  { metric: 'DSP Toolkit', label: 'NHS supplier assessments completed and maintained' },
  { metric: 'ISO 27001', label: 'Certification achieved alongside sector-specific compliance' },
];

export default function HealthTechPage() {
  useScrollAnimation();

  return (
    <div className="min-h-screen bg-[#050a0f] text-[#f5f5f8] noise-overlay-animated">
      <Navbar />

      {/* Hero */}
      <section className="relative pt-32 pb-20 lg:pt-40 lg:pb-28 overflow-hidden">
        <div className="absolute inset-0 pointer-events-none">
          <div className="orb orb-blue" style={{ width: '600px', height: '600px', top: '-150px', left: '-100px', opacity: 0.3 }} />
          <div className="orb orb-teal" style={{ width: '400px', height: '400px', bottom: '0', right: '5%', opacity: 0.2 }} />
        </div>
        <div className="relative z-10 max-w-[1440px] mx-auto px-6 lg:px-12">
          <div className="flex items-center gap-3 mb-6 fade-in-up">
            <Link href="/" className="font-mono-brand text-[0.65rem] text-[#555] uppercase tracking-widest hover:text-[#80edfa] transition-colors">Home</Link>
            <span className="text-[#333]">/</span>
            <span className="font-mono-brand text-[0.65rem] text-[#555] uppercase tracking-widest">Industries</span>
            <span className="text-[#333]">/</span>
            <span className="font-mono-brand text-[0.65rem] text-[#80edfa] uppercase tracking-widest">HealthTech & MedTech</span>
          </div>
          <div className="max-w-[900px]">
            <p className="font-mono-brand text-[0.7rem] text-[#80edfa] uppercase tracking-widest mb-4 fade-in-up" style={{ transitionDelay: '0.05s' }}>
              [HealthTech & MedTech]
            </p>
            <h1 className="font-heading text-[clamp(2.5rem,6vw,4.5rem)] text-[#f5f5f8] font-medium leading-[1.05] mb-6 fade-in-up" style={{ transitionDelay: '0.1s' }}>
              Patient data security<br />
              <span className="text-[#80edfa]">without slowing you down.</span>
            </h1>
            <p className="text-[#aaa] text-lg leading-relaxed max-w-[680px] fade-in-up" style={{ transitionDelay: '0.15s' }}>
              HIPAA, GDPR, NHS DSP Toolkit, and medical device security requirements demand rigorous, continuous security, not annual checkbox exercises. TechSonar embeds senior engineers into HealthTech and MedTech organisations to protect patient data and satisfy regulators without blocking product delivery.
            </p>
          </div>
        </div>
      </section>

      {/* Outcomes strip */}
      <section className="border-t border-[#222] py-12 bg-[#0a0f14]">
        <div className="max-w-[1440px] mx-auto px-6 lg:px-12">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-8">
            {outcomes?.map((o, i) => (
              <div key={i} className="fade-in-up" style={{ transitionDelay: `${i * 0.07}s` }}>
                <p className="font-heading text-2xl text-[#80edfa] font-medium mb-2">{o?.metric}</p>
                <p className="text-xs text-[#555] leading-relaxed">{o?.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Challenges */}
      <section className="border-t border-[#222] py-20 lg:py-28">
        <div className="max-w-[1440px] mx-auto px-6 lg:px-12">
          <div className="mb-12 fade-in-up">
            <p className="font-mono-brand text-[0.65rem] text-[#555] uppercase tracking-widest mb-2">The challenge</p>
            <h2 className="font-heading text-[clamp(1.8rem,4vw,3rem)] text-[#f5f5f8] font-medium leading-tight max-w-[600px]">
              What makes health data security different.
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-px bg-[#222]">
            {challenges?.map((c, i) => (
              <div key={i} className="bg-[#050a0f] p-8 lg:p-10 fade-in-up" style={{ transitionDelay: `${i * 0.07}s` }}>
                <p className="font-mono-brand text-[0.65rem] text-[#80edfa] uppercase tracking-widest mb-4">{c?.tag}</p>
                <h3 className="font-heading text-xl text-[#f5f5f8] font-medium mb-3">{c?.title}</h3>
                <p className="text-sm text-[#666] leading-relaxed">{c?.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Services */}
      <section className="border-t border-[#222] py-20 lg:py-28 bg-[#0a0f14]">
        <div className="max-w-[1440px] mx-auto px-6 lg:px-12">
          <div className="mb-12 fade-in-up">
            <p className="font-mono-brand text-[0.65rem] text-[#555] uppercase tracking-widest mb-2">How we help</p>
            <h2 className="font-heading text-[clamp(1.8rem,4vw,3rem)] text-[#f5f5f8] font-medium leading-tight max-w-[600px]">
              Services built for health data environments.
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px bg-[#222]">
            {services?.map((s, i) => (
              <Link
                key={i}
                href={s?.href}
                className="group bg-[#0a0f14] p-8 hover:bg-[#111519] transition-colors duration-300 fade-in-up"
                style={{ transitionDelay: `${i * 0.07}s` }}
              >
                <div className="flex items-start justify-between mb-4">
                  <h3 className="font-heading text-lg text-[#f5f5f8] font-medium group-hover:text-[#80edfa] transition-colors pr-4">{s?.label}</h3>
                  <svg className="w-4 h-4 text-[#333] group-hover:text-[#80edfa] transition-colors flex-shrink-0 mt-1" viewBox="0 0 16 16" fill="none">
                    <path d="M3 13L13 3M13 3H6M13 3V10" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </div>
                <p className="text-sm text-[#555] leading-relaxed group-hover:text-[#888] transition-colors">{s?.desc}</p>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="border-t border-[#222] py-20 lg:py-28">
        <div className="max-w-[1440px] mx-auto px-6 lg:px-12">
          <div className="max-w-[700px] fade-in-up">
            <p className="font-mono-brand text-[0.65rem] text-[#80edfa] uppercase tracking-widest mb-4">[Get started]</p>
            <h2 className="font-heading text-[clamp(2rem,4vw,3.5rem)] text-[#f5f5f8] font-medium leading-tight mb-6">
              Protect patient data. Satisfy regulators. Ship faster.
            </h2>
            <p className="text-[#aaa] text-lg leading-relaxed mb-8">
              Whether you are preparing for a HIPAA audit, an NHS DSP Toolkit assessment, or a GDPR review, TechSonar can get you there without slowing your product team down.
            </p>
            <div className="flex flex-wrap gap-4">
              <Link href="/contact" className="btn-primary">Book a discovery call</Link>
              <Link href="/services/compliance" className="px-6 py-3 border border-[#333] text-[#aaa] text-sm uppercase tracking-wide hover:border-[#80edfa] hover:text-[#80edfa] transition-colors font-sans">
                View Compliance Services
              </Link>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
